import React, { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Icon } from "@iconify/react";

// Upload sections; `tag` is the value stored in project.imageTags.
const SECTIONS = [
  {
    tag: "actual",
    title: "Actual Images",
    hint: "Photos of the finished site",
    icon: "mdi:camera-outline",
  },
  {
    tag: "render",
    title: "3D Images (Renders)",
    hint: "3D visualisations and renders",
    icon: "catppuccin:3d",
  },
];

const LARGE_FILE = 3 * 1024 * 1024;

const Thumb = ({ file, onRemove, onMove, moveLabel }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  return (
    <div className="relative h-24 rounded overflow-hidden border bg-gray-50">
      {url && (
        <img src={url} alt={file.name} className="w-full h-full object-cover" />
      )}
      {file.size > LARGE_FILE && (
        <Icon
          icon="fluent-emoji-flat:warning"
          title="Larger than 3MB"
          className="absolute text-2xl top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
        />
      )}
      <button
        type="button"
        title="Remove"
        onClick={onRemove}
        className="absolute top-1 right-1 bg-red-500 text-white rounded-full size-5 grid place-content-center"
      >
        <Icon icon="mdi:trash-can-outline" className="size-3" />
      </button>
      <button
        type="button"
        title={moveLabel}
        onClick={onMove}
        className="absolute bottom-1 left-1 right-1 bg-black/60 hover:bg-black/80 text-white text-[10px] rounded py-0.5"
      >
        {moveLabel}
      </button>
    </div>
  );
};

const UploadSection = ({ section, items, onDrop, onRemove, onMove, other }) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { "image/jpeg": [], "image/png": [] },
    onDrop,
  });

  return (
    <div className="flex flex-col border rounded-lg bg-white p-3 min-w-0">
      <div className="flex items-center justify-between mb-2">
        <h3 className="flex items-center gap-2 font-semibold text-sm">
          <Icon icon={section.icon} className="text-lg text-primary-500" />
          {section.title}
        </h3>
        <span className="text-xs text-gray-500">
          {items.length} image{items.length === 1 ? "" : "s"}
        </span>
      </div>
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-4 text-center text-sm cursor-pointer transition-colors ${
          isDragActive
            ? "border-primary-500 bg-teal-50"
            : "border-gray-300 hover:border-primary-500"
        }`}
      >
        <input {...getInputProps()} />
        <p>
          Drop {section.title.toLowerCase()} here or{" "}
          <span className="text-primary-500">browse</span>
        </p>
        <p className="text-xs text-gray-400 mt-1">{section.hint}</p>
      </div>
      <div className="mt-3 h-64 overflow-y-auto">
        {items.length === 0 ? (
          <div className="h-full grid place-content-center text-xs text-gray-400">
            No {section.title.toLowerCase()} added
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {items.map(({ file, index }) => (
              <Thumb
                key={index}
                file={file}
                onRemove={() => onRemove(index)}
                onMove={() => onMove(index, other.tag)}
                moveLabel={`Move to ${other.tag === "render" ? "3D" : "Actual"}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// `uploads` is [{ file, tag }] in upload order; split into projectImages and
// imageTags when submitting.
const TaggedImageUploader = ({ uploads, onChange }) => {
  const add = (tag) => (files) =>
    onChange([...uploads, ...files.map((file) => ({ file, tag }))]);
  const remove = (index) => onChange(uploads.filter((_, i) => i !== index));
  const move = (index, tag) =>
    onChange(uploads.map((u, i) => (i === index ? { ...u, tag } : u)));

  const largeFiles = uploads.filter((u) => u.file.size > LARGE_FILE);

  return (
    <div className="mt-4">
      {largeFiles.length > 0 && (
        <div className="bg-red-50 text-xs border border-red-300 rounded-lg p-3 mb-3">
          <p className="font-semibold mb-1">
            {largeFiles.length} file{largeFiles.length === 1 ? " is" : "s are"}{" "}
            larger than 3MB:
          </p>
          <p className="break-all">
            {largeFiles.map((u) => u.file.name).join(", ")}
          </p>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4">
        {SECTIONS.map((section) => (
          <UploadSection
            key={section.tag}
            section={section}
            other={SECTIONS.find((s) => s.tag !== section.tag)}
            items={uploads
              .map((u, index) => ({ ...u, index }))
              .filter((u) => u.tag === section.tag)}
            onDrop={add(section.tag)}
            onRemove={remove}
            onMove={move}
          />
        ))}
      </div>
    </div>
  );
};

export const toUploadFormData = (formDataToSend, uploads) => {
  formDataToSend.append("imageTags", JSON.stringify(uploads.map((u) => u.tag)));
  uploads.forEach(({ file, tag }, index) => {
    formDataToSend.append(`projectImages_${index}`, file);
    formDataToSend.append(`imageTag_${index}`, tag);
  });
};

export default TaggedImageUploader;
